import i18nFactory from '@yandex-int/i18n';
import { FetchResult } from 'apollo-boost';
import { compact, forEach, get, isArray, isEmpty, isEqual, mergeWith, some, uniq, values } from 'lodash';
import { Action } from 'redux';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from, merge, of, timer } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs/Rx';

import {
    changeLocation,
    changeLocationHard,
    updateCryptaSegments,
    updateIndoorMapData,
    updateOutdoorMapData,
    updateRegions,
    updateStringDicts
} from '../../actions/common';
import { updateDatesIntervalEditors } from '../../actions/datesIntervalEditor';
import { closeEditPageItemNameEditor, showEditPageItemNameEditorErrors } from '../../actions/editPageItemNameEditor';
import { goToBannersEditForCreation, goToGroupsEdit } from '../../actions/grid-screen';
import {
    OPEN_GROUP_FILTER_INSPECTOR,
    OpenGroupFilterInspectorAction,
    setFilterFeedCategories,
    updateFeedFilterSchema
} from '../../actions/groupFilterInspector';
import {
    HANDLE_GROUPS_EDIT_MASS_ACTIONS,
    HandleGroupsEditMassActions,
    openMinusPhraseGroupsMassActions
} from '../../actions/groups-edit/groups-edit-mass-actions';
import {
    PERFORMANCE_SET_VISIBLE_GROUP_BANNER_POPUP_FEED,
    SetVisibleGroupBannerPopupFeedAction,
    updateFeeds
} from '../../actions/groups-edit/performance';
import {
    FETCH_RECOMMENDATIONS_FOR_GROUPS_EDIT,
    fetchRecommendationsForGroupsEdit,
    FetchRecommendationsForGroupsEditAction,
    OPEN_RECOMMENDATIONS_MODAL_FOR_GROUPS_EDIT,
    OpenRecommendationsModalForGroupsEditAction,
    updateRecommendationsForGroupsEdit
} from '../../actions/groups-edit/recommendations';
import {
    APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT,
    ApplySelectorWizardForGroupEdit,
    CANCEL_GROUP_EDITOR_REGIONS,
    CancelGroupEditorRegions,
    changeGroupEditPageStatus,
    changeGroupName,
    changeGroupRetargeting,
    clearErrorPageGroupTags,
    clearErrorsGroupEditorRegions,
    clearErrorTargetTags,
    CREATE_MC_BANNER_AD_GROUP,
    CREATE_SMART_AD_GROUP,
    CREATE_TEXT_AD_GROUP,
    createMcBannerAdGroup as createMcBannerAdGroupAction,
    CreateMcBannerAdGroupAction,
    CreateSmartAdGroupAction,
    createTextAdGroup as createTextAdGroupAction,
    CreateTextAdGroupAction,
    DeleteRetargetingConditionFromGroup,
    deleteRetargetingConditionFromGroup,
    GET_CAMPAIGN_BASIC_DATA,
    GET_CPC_GROUPS_FOR_EDIT,
    GET_CPM_GROUPS_FOR_EDIT,
    GET_GROUPS_FOR_EDIT,
    GET_INTERNAL_GROUPS_FOR_EDIT,
    GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT,
    GetCampaignBasicDataAction,
    GetCpcGroupsForEditAction,
    getCpcGroupsForEditAction,
    GetCpmGroupsForEditAction,
    getCpmGroupsForEditAction,
    GetGroupsForEditAction,
    getInternalGroupsForEditAction,
    GetInternalGroupsForEditAction,
    getRetargetingConditionsForGroupsEdit,
    GetRetargetingConditionsForGroupsEditAction,
    GROUP_DATA_UPDATE_KEYWORDS_FROM_EDITOR,
    groupApplyChangedMinusPhraseEditor,
    groupDataUpdateKeywords,
    GroupDataUpdateKeywordsFromEditorAction,
    LEAVE_GROUPS_EDIT,
    LeaveGroupsEdit,
    moveNewGroupToCreated,
    OPEN_EDIT_GROUP_PHRASES_SELECTOR_BETA,
    OPEN_GROUP_AUDIENCE_TARGETINGS_EDITOR,
    OPEN_GROUP_PI_TAGS_EDITOR,
    OPEN_GROUP_RETARGETING_EDITOR,
    OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT,
    openGroupAudienceTargetingsEditor,
    OpenGroupAudienceTargetingsEditorAction,
    OpenGroupPITagsEditorAction,
    OpenGroupRetargetingEditor,
    OpenPhrasesSelectorWizardForGroupsEdit,
    RESET_GROUPS_EDIT,
    resetGroupsEdit,
    ResetGroupsEdit,
    resetRetargetingErrors,
    SAVE_GROUP_EDITOR_REGIONS,
    SAVE_GROUP_MINUS_PHRASES_EDITOR,
    SAVE_GROUP_NAME_EDITOR_CHANGES,
    SAVE_GROUP_RATE_CORRECTIONS_EDITOR,
    SAVE_GROUP_RETARGETING_EDITOR,
    SAVE_GROUPS,
    SAVE_PHRASES_SELECTOR_PHRASES,
    SaveGroupEditorRegions,
    saveGroupEditorRegions,
    SaveGroupMinusPhrasesEditorAction,
    SaveGroupNameEditorChangesAction,
    saveGroupPITagsEditor,
    SaveGroupRateCorrectionsEditorAction,
    SaveGroupRetargetingEditor,
    saveGroups,
    SaveGroupsAction,
    SavePhrasesSelectorPhrasesAction,
    showGroupEditError,
    ShowGroupEditErrorAction,
    showGroupEditorRegionsErrors,
    showGroupsClientValidationErrors,
    showGroupsPartialSaveResult,
    UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES,
    UPDATE_GROUP_EDITOR_REGIONS,
    UPDATE_GROUP_TYPE,
    UPDATE_SHOW_CONDITION_TYPE,
    updateAudienceTargetings,
    updateDataForEditGroups,
    UpdateGroupAudienceCryptaAudioGenres,
    updateGroupAudienceCryptaAudioGenres,
    updateGroupEditorRegions,
    updateGroupIndoorMap,
    updateGroupMinusPhrases,
    updateGroupOutdoorMap,
    updateGroupRateCorrections,
    UpdateGroupTypeAction,
    updateProductRestrictionIdAction,
    updateProductsList,
    updateRetargetingConditions,
    updateRetargetingConditionsForEditGroups,
    UpdateRetargetingConditionsForEditGroupsAction,
    UpdateShowConditionTypeAction
} from '../../actions/groupsEdit';
import {
    clearIndoorErrors,
    closeIndoorMapEditor,
    SAVE_INDOOR_MAP_ACTION,
    SaveIndoorMapAction
} from '../../actions/indoorMapEditor';
import { keywordsEditorSetErrors, keywordsEditorUpdate } from '../../actions/keywordsEditor';
import {
    changeMinusPhrasesEditor,
    closeMinusPhrasesEditor,
    showEditPageMinusPhrasesEditorErrors
} from '../../actions/minusPhrasesEditor';
import {
    clearOutdoorErrors,
    closeOutdoorMapEditor,
    SAVE_OUTDOOR_MAP_ACTION,
    SaveOutdoorMapAction
} from '../../actions/outdoorMapEditor';
import {
    phrasesSelectorGetStats,
    phrasesSelectorHideEditor,
    phrasesSelectorOpenEditor,
    phrasesSelectorSetGroupKeywords
} from '../../actions/phrases-selector';
import { phrasesSelectorClearData } from '../../actions/phrases-selector/persistentData';
import {
    addPhrasesStartInputItems,
    closePhrasesSelectorWizard,
    openPhrasesSelectorWizard
} from '../../actions/phrasesSelectorWizard';
import {
    closePITagsEditor,
    openPITagsEditor,
    PAGE_GROUP_TAGS_CHANGE,
    PageGroupTagsChange,
    SAVE_PI_TAGS_EDITOR,
    SavePITagsEditor,
    TARGET_TAGS_CHANGE,
    TargetTagsChange
} from '../../actions/piTagsEditor';
import {
    clearRateCorrectionsEditor,
    closeRateCorrectionsEditor,
    resetRateCorrectionsEditors,
    updateRateCorrectionsEditorValidation
} from '../../actions/rateCorrectionsEditor';
import { setRecommendationsModalDataAction } from '../../actions/recommendations-modal';
import {
    cancelChangesRegionsEditor,
    closeRegionsEditor,
    ResetRegionsEditor,
    resetRegionsEditor
} from '../../actions/regionsEditor';
import {
    ADD_RETARGETING_LIST_ITEM,
    CHANGE_RETARGETING_LIST_ITEM_SELECTION,
    CHANGE_RETARGETING_LIST_SELECTION,
    ChangeRetargetingListItemSelection,
    ChangeRetargetingListSelection,
    closeRetargetingEditor,
    DELETE_RETARGETING_LIST_ITEM,
    DeleteRetargetingListItem,
    openRetargetingEditor,
    resetRetargetingEditors,
    UPDATE_RETARGETING_LIST_ITEM,
    UpdateRetargetingListItem
} from '../../actions/retargetingEditor';
import { updateTagCloudTargetings } from '../../actions/tagCloudTargeting';
import {
    CLOSE_AUDIENCE_TARGETINGS,
    closeAudienceTargetings,
    CloseAudienceTargetingsAction,
    openAudienceTargetingsEditor,
    RESET_AUDIENCE_TARGETINGS,
    ResetAudienceTargetings,
    SAVE_AUDIENCE_TARGETINGS,
    SaveAudienceTargetings
} from '../../actions/targetings/audience';
import {
    closeCryptaAudioGenresEditor,
    SAVE_CRYPTA_AUDIO_GENRES,
    SaveCryptaAudioGenres
} from '../../actions/targetings/cryptaAudioGenres';
import { closeCryptaBehavioralEditor, resetCryptaBehavioralEditor } from '../../actions/targetings/cryptaBehavioral';
import { closeCryptaGroupFamilyEditor, resetCryptaGroupFamilyEditor } from '../../actions/targetings/cryptaGroupFamily';
import { closeCryptaSocialDemoEditor, resetCryptaSocialDemoEditor } from '../../actions/targetings/cryptaSocialDemo';
import { closeInterestsTargetingEditor, resetInterestsTargetingEditor } from '../../actions/targetings/interestsEditor';
import {
    openAudienceRetargetingEditor,
    resetAudienceRetargetingEditor
} from '../../actions/targetings/retargetingConditionsForAudienceEditor';
import { initTargetTagsEditors, resetTargetTagsEditors } from '../../actions/targetTagsEditor';
import { FetchMinusPhraseLibraryDataResult } from '../../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { GROUP_PRIORITY } from '../../constants';
import { mapCampaignRowTypeToCampaignType } from '../../mappers/common/common';
import { mapCryptaSegmentsFromApiFormat } from '../../mappers/cryptaSegments/cryptaSegments';
import { mapCampaignTypeToCalcType } from '../../mappers/filter/filter';
import {
    getCategoryGroupIdsMap,
    getGroupsValueFromOpenedEditors,
    getNewGroupName,
    getTargetTagsEditorsState,
    makeGroupsForEditNewCpmGroupData,
    makeGroupsForEditNewGroupData,
    mapCpmGroupsDataToApiFormat,
    mapFeedFilterSchemaFromApiResponse,
    mapFeeds,
    mapGroupEditRecommendationsToQueryResponse,
    mapGroupsForEditQueryResultToQueryResponse,
    mapGroupsToSaveGroupsInput,
    mapRetargetingConditionsQueryResultToQueryResponse
} from '../../mappers/group/group';
import {
    mapDatesIntervalEditorsDataFromInternalGroups,
    mapInternalGroupValueToTagCloudTargetings
} from '../../mappers/internalGroup';
import { mapFetchResultToLibraryItem } from '../../mappers/minusPhrases';
import { mapPhraseToPhrasesSelectorListItem } from '../../mappers/phrasesSelectorWizard';
import {
    mapIndoorPlacementsMapFromApiFormat,
    mapOutdoorPlacementsMapFromApiFormat
} from '../../mappers/placementsMap/placementsMap';
import { mapPricePackagesDataForGroup } from '../../mappers/pricePackages/pricePackages';
import {
    getAllowedRateCorrectionsDataForGroup,
    mapEditorRateCorrectionsToRateCorrections,
    mapRatesCorrectionsConstantsFromApiFormat
} from '../../mappers/rateCorrections/rateCorrections';
import { isGroupSupportsRecommendationsOnEditBannerPage } from '../../mappers/recommendation';
import {
    mapRetargetingConditionFromApiFormat,
    mapRetargetingEditorValueToGroupRetargetingValue
} from '../../mappers/retargeting/retargeting';
import { awaitCommonData } from '../../operators';
import { getAllCryptaSegmentsByType } from '../../selectors/cryptaSegments/cryptaSegments';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import {
    getGroupModalRecommendations,
    selectAccessRights,
    selectClientFeatures,
    selectEditableGroupTypes,
    selectEditGroupValueInfo,
    selectOperatorFeatures
} from '../../selectors/groupEdit/group/group';
import { selectEditGroupCurrentKeywords, selectKeywordsEditorData } from '../../selectors/keywordsEditor';
import {
    selectEditGroupCurrentMinusPhraseLibraries,
    selectEditGroupCurrentMinusPhrases,
    selectMinusPhrasesEditorById
} from '../../selectors/minusPhrases/minusPhrases';
import { filterGroupIndoorMapData } from '../../selectors/placementsMap/placementsMap';
import { confirm } from '../../service/Confirm/Confirm';
import { isUserProfileEmpty } from '../../service/cpm/cpm';
import { calculateOpenedCategory } from '../../service/CryptaHelpers/CryptaHelpers';
import {
    extractSavedGroupIds,
    extractUpdatedGroupIds,
    extractValidationErrors
} from '../../service/ErrorHelpers/ErrorHelpers';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import {
    getGroupNameEditorValidationResult,
    getGroupsOpenedEditorsValidationResult,
    getSaveGroupsValidationErrors
} from '../../service/GroupEditErrors/ClientGroupEditErrors/ClientGroupEditErrors';
import { ExternalGroupValidationSchema } from '../../service/GroupEditErrors/ExternalGroupValidationSchema/ExternalGroupValidationSchema';
import {
    createCpmGroupUpdateHandleErrorMapper,
    createSmartGroupAddHandleErrorMapper
} from '../../service/GroupEditErrors/GroupsBackToFrontErrorMappers';
import { GroupIdsForGroupsEditHelpers } from '../../service/GroupIdsForGroupsEditHelpers';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { isInternalGroup } from '../../service/InternalGroupHelpers/InternalGroupHelpers';
import {
    getGroupKeywordsEditorId,
    getUniqJoinedKeywordsData,
    parsePhrasesFromStringList
} from '../../service/keywordsEditor';
import { LangUtils } from '../../service/Lang/Lang';
import { createMcBannerAdGroup, createTextAdGroup } from '../../service/mutationHelpers/mutationHelpers';
import { validateRateCorrectionEditor } from '../../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { AudienceTargetingsType } from '../../typings/audienceTargetings';
import { ErrorsEdit, PageStatus, Region } from '../../typings/common';
import { CampaignsCalcTypeFilter, CampaignType, GroupType } from '../../typings/domain';
import { DnaEpic, DnaEpicAction } from '../../typings/epic';
import {
    GdCampaignType,
    GroupsQueryForEdit_client_feeds_rowset,
    PlacementTypeInput,
    SaveAdGroup,
    UpdateCpmAdGroup,
    WebGoalTypeInput
} from '../../typings/graphql-grid';
import { GridScreenUrlSearchQueryParams, NoticeType } from '../../typings/grid';
import {
    GroupId,
    GroupsCategories,
    GroupsEditMassActionItemType,
    GroupsScreenUrlSearchQueryParams
} from '../../typings/groups';
import {
    DataForEditInternalGroups,
    EditGroupData,
    getEditGroupDataValueWithKeywords,
    getEditGroupDataValueWithMinusPhrases,
    getEditGroupDataValueWithRegions,
    MassActionId
} from '../../typings/groupsEditState';
import { PhrasesSelectorWizardId } from '../../typings/phrases';
import { RecommendationsModalState } from '../../typings/recommendations';
import { State, StringDicts } from '../../typings/state';
import { TargetPlatformTags } from '../../typings/targetTagsEditor';

import * as keyset from './groupsEdit.i18n';

const i18n = i18nFactory(keyset);

const updateShowConditionTypeEpic: Epic<Action, Action, State, EpicDeps> = action$ => {
    return action$.pipe(
        ofType(UPDATE_SHOW_CONDITION_TYPE),
        mergeMap((action: UpdateShowConditionTypeAction) => {
            return [
                closeRateCorrectionsEditor(action.groupId),
                getRetargetingConditionsForGroupsEdit(action.ulogin),
                clearRateCorrectionsEditor(action.groupId)
            ];
        })
    );
};

const initGroupsEditors = (groupsData: DataForEditInternalGroups, stringDicts?: StringDicts): Action[] => {
    const datesIntervalEditorsData = mapDatesIntervalEditorsDataFromInternalGroups(groupsData.groups);

    const tagCloudTargetingEditors = mapInternalGroupValueToTagCloudTargetings(groupsData.groups, stringDicts);

    return compact([
        datesIntervalEditorsData && updateDatesIntervalEditors(datesIntervalEditorsData),
        tagCloudTargetingEditors && updateTagCloudTargetings(tagCloudTargetingEditors)
    ]);
};

const getCampaignBasicDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_CAMPAIGN_BASIC_DATA),
        awaitCommonData(action$, store$),
        mergeMap((action: GetCampaignBasicDataAction) => {
            const { campaignId, login } = action;
            const state = store$.value;
            const {
                commonData: {
                    client: { translocalGeotreeType, workCurrency },
                    features: { isTextBannerInterestsRetCondEnabled, isZeroSpeedPageForGeoproductEnabled }
                },
                editGroups: { isNew }
            } = state;
            const clientFeatures = selectClientFeatures(state);

            return forkJoin(
                deps.groupsDataProvider.getCampaignBasicDataForGroupEdit(login, campaignId),
                RegionsHelpers.fetchRegions(),
                deps.groupsDataProvider.getRetargetingConditions(login)
            ).pipe(
                mergeMap(([campaignResult, regionsResult, retargetingConditionsResult]):
                    | Observable<Action>
                    | Array<Action> => {
                    const [campaignData] = campaignResult.data.client.campaigns.rowset;

                    const features = selectClientFeatures(state);

                    // DIRECT-99993 для текстовых кампаний создаем группу, нужно будет убрать в DIRECT-99992
                    if (campaignData.type === GdCampaignType.TEXT && !features.isHonestAddTextAdGroupAllowed) {
                        return of(createTextAdGroupAction(campaignId, getNewGroupName(), true));
                    }

                    if (campaignData.type === GdCampaignType.MCBANNER) {
                        return of(createMcBannerAdGroupAction(campaignId, getNewGroupName(), true));
                    }

                    const isDeviceModifiersAllowed =
                        campaignData.type === GdCampaignType.CPM_BANNER || clientFeatures.isCpcDeviceModifiersAllowed;
                    const {
                        allowedBidModifierTypesByAdGroupTypes,
                        allowedBidModifiersValues,
                        clientLimits
                    } = campaignResult.data.client.clientConstants;
                    const allowedRateCorrectionsData = getAllowedRateCorrectionsDataForGroup(
                        allowedBidModifierTypesByAdGroupTypes
                    );
                    const rateCorrectionsConstants = mapRatesCorrectionsConstantsFromApiFormat(
                        allowedBidModifiersValues,
                        Boolean(isDeviceModifiersAllowed)
                    );
                    const regionTree: Region[] = regionsResult
                        ? RegionsHelpers.getRegionsTree(
                              regionsResult,
                              LangUtils.getCurrentLang(),
                              translocalGeotreeType
                          )
                        : [];
                    const retargetingConditions = retargetingConditionsResult.data.client.retargetingConditions.rowset.map(
                        retargetingCondition => mapRetargetingConditionFromApiFormat(retargetingCondition, login)
                    );

                    if (campaignData.type === GdCampaignType.CPM_PRICE) {
                        const emptyResponse = Promise.resolve(undefined);
                        const hasDefaultAdGroup = get(campaignData, 'hasDefaultAdGroup');
                        const pricePackage = mapPricePackagesDataForGroup(campaignData);

                        return forkJoin(
                            deps.groupsDataProvider.getProducts({
                                campaignId: campaignData.id,
                                calcType: CampaignsCalcTypeFilter.CPM,
                                type: CampaignType.CPM_PRICE,
                                currency: workCurrency
                            }),
                            deps.groupsDataProvider.getCryptaSegments({
                                name: ''
                            }),
                            hasDefaultAdGroup
                                ? deps.groupsDataProvider.getGroupEditRegionTree(login, [campaignId])
                                : emptyResponse
                        ).pipe(
                            mergeMap(
                                ([productsList, cryptaSegmentsResult, regionTrimmedTrees]): Array<Action> => {
                                    const regionTrimmedTree =
                                        regionTrimmedTrees && regionTrimmedTrees[campaignId]
                                            ? regionTrimmedTrees[campaignId]
                                            : undefined;
                                    const cryptaSegments = mapCryptaSegmentsFromApiFormat(cryptaSegmentsResult);

                                    const cpmGroupsForEditData = makeGroupsForEditNewCpmGroupData(
                                        campaignData,
                                        regionTrimmedTree || regionTree,
                                        allowedRateCorrectionsData,
                                        clientLimits.keywordsCountLimit,
                                        productsList,
                                        rateCorrectionsConstants,
                                        false,
                                        pricePackage,
                                        regionTrimmedTree
                                    );

                                    return [
                                        updateCryptaSegments(cryptaSegments),
                                        updateProductsList(productsList),
                                        updateRegions(regionsResult),
                                        updateDataForEditGroups(cpmGroupsForEditData)
                                    ];
                                }
                            )
                        );
                    }

                    // Для CPM_BANNER мы ходим за списком продуктов и по нему рисуем создание
                    if (campaignData.type === GdCampaignType.CPM_BANNER) {
                        return forkJoin(
                            deps.groupsDataProvider.getProducts({
                                campaignId: campaignData.id,
                                calcType: CampaignsCalcTypeFilter.CPM,
                                type: CampaignType.CPM_BANNER,
                                currency: workCurrency
                            }),
                            deps.groupsDataProvider.getCryptaSegments({
                                name: ''
                            }),
                            deps.groupsDataProvider.getPlacements({
                                placementType: PlacementTypeInput.OUTDOOR
                            }),
                            deps.groupsDataProvider.getPlacements({
                                placementType: PlacementTypeInput.INDOOR
                            })
                        ).pipe(
                            mergeMap(
                                ([productsList, cryptaSegmentsResult, placementsOutdoor, placementsIndoor]): Array<
                                    Action
                                > => {
                                    const additiveActions = [];
                                    const cryptaSegments = mapCryptaSegmentsFromApiFormat(cryptaSegmentsResult);

                                    const cpmGroupsForEditData = makeGroupsForEditNewCpmGroupData(
                                        campaignData,
                                        regionTree,
                                        allowedRateCorrectionsData,
                                        clientLimits.keywordsCountLimit,
                                        productsList,
                                        rateCorrectionsConstants
                                    );

                                    const newGroupsValuesList = values(cpmGroupsForEditData.groups);
                                    const geoGroupsList = newGroupsValuesList.filter(
                                        group => group.value.groupType === GroupType.CPM_GEOPRODUCT
                                    );

                                    if (isZeroSpeedPageForGeoproductEnabled && geoGroupsList.length) {
                                        const defaultTargetTagsEditors = getTargetTagsEditorsState(
                                            geoGroupsList,
                                            productsList,
                                            [TargetPlatformTags.APP_NAVI, TargetPlatformTags.APP_METRO]
                                        );
                                        additiveActions.push(initTargetTagsEditors(defaultTargetTagsEditors));
                                    }

                                    return [
                                        ...additiveActions,
                                        updateOutdoorMapData(mapOutdoorPlacementsMapFromApiFormat(placementsOutdoor)),
                                        updateIndoorMapData(mapIndoorPlacementsMapFromApiFormat(placementsIndoor)),
                                        updateCryptaSegments(cryptaSegments),
                                        updateProductsList(productsList),
                                        updateRegions(regionsResult),
                                        updateDataForEditGroups(cpmGroupsForEditData),
                                        openGroupAudienceTargetingsEditor(Object.keys(cpmGroupsForEditData.groups)[0])
                                    ];
                                }
                            )
                        );
                    }

                    // Для отображения пустой группы до ее создания в базе данных
                    // Необходимо только для смарт-групп, так как их создание состоит из нескольких шагов
                    const groupsForEditData = makeGroupsForEditNewGroupData(
                        campaignData,
                        regionTree,
                        retargetingConditions,
                        allowedRateCorrectionsData,
                        rateCorrectionsConstants,
                        clientLimits.keywordsCountLimit,
                        clientFeatures
                    );

                    const updateActions = [updateRegions(regionsResult), updateDataForEditGroups(groupsForEditData)];

                    const isFetchCrypta = GroupsHelpers.getFetchCryptaFlags(
                        groupsForEditData,
                        isTextBannerInterestsRetCondEnabled
                    );

                    // todo удалить после открытия фичи на 100% DIRECT-109443 (удалить использование фичи)
                    if (isFetchCrypta && features.isHonestAddTextAdGroupAllowed) {
                        return from(
                            deps.groupsDataProvider.getCryptaSegments({
                                name: '',
                                type: [WebGoalTypeInput.interests]
                            })
                        ).pipe(
                            mergeMap(cryptaSegmentsResult => {
                                return [
                                    updateCryptaSegments(mapCryptaSegmentsFromApiFormat(cryptaSegmentsResult)),
                                    ...updateActions
                                ];
                            })
                        );
                    }

                    // FIXME: Оторвать внутренние группы от внешних
                    //        @alex-sannikov
                    //        https://st.yandex-team.ru/DIRECT-100081
                    if (isNew && isInternalGroup(campaignData.type)) {
                        return [...initGroupsEditors(groupsForEditData as DataForEditInternalGroups), ...updateActions];
                    }

                    return updateActions;
                }),
                catchError((error: ApiMethodExecutingError) => of(showGroupEditError(error)))
            );
        })
    );
};

const getGroupsForEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    const { login } = store$.value.commonData;

    return action$.pipe(
        ofType(GET_GROUPS_FOR_EDIT),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: GetGroupsForEditAction): Observable<Action> => {
                const state = store$.value;
                const {
                    editGroups: { groupsIds, campaignsIds }
                } = state;
                const { accessRights } = state.commonData;

                if (groupsIds.length === 0 || campaignsIds.length === 0) {
                    const url = UrlHelpers.getGroupsListUrl();
                    return of(
                        changeLocation(url, {
                            ulogin: login
                        })
                    );
                }

                return forkJoin(
                    deps.groupsDataProvider.getGroups(action.login, groupsIds, campaignsIds),
                    RegionsHelpers.fetchRegions(),
                    deps.groupsDataProvider.getUaTraits(
                        state.commonData.stringDicts,
                        accessRights && accessRights.canEditInternalAds
                    )
                ).pipe(
                    mergeMap(([groupsData, regionsData, stringDictsResult]) => {
                        const {
                            data: {
                                client: {
                                    adGroups: {
                                        rowset: [
                                            {
                                                campaign: { type }
                                            }
                                        ]
                                    }
                                }
                            }
                        } = groupsData;

                        let groupsForEditAction;

                        // FIXME: Оторвать внутренние группы от внешних
                        //        @alex-sannikov
                        //        https://st.yandex-team.ru/DIRECT-100081
                        if (isInternalGroup(type)) {
                            groupsForEditAction = getInternalGroupsForEditAction;
                        } else {
                            groupsForEditAction =
                                mapCampaignTypeToCalcType(mapCampaignRowTypeToCampaignType(type)) ===
                                CampaignsCalcTypeFilter.CPM
                                    ? getCpmGroupsForEditAction
                                    : getCpcGroupsForEditAction;
                        }

                        return [
                            resetRegionsEditor(),
                            updateRegions(regionsData),
                            updateStringDicts(stringDictsResult),
                            groupsForEditAction(login, groupsData)
                        ];
                    }),
                    catchError((error: ApiMethodExecutingError) => {
                        return of(showGroupEditError(error));
                    })
                );
            }
        )
    );
};

const updateGroupTypeEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(UPDATE_GROUP_TYPE),
        mergeMap(
            ({ groupId, group }: UpdateGroupTypeAction): DnaEpicAction => {
                const state = store$.value;
                const { productsList, groups } = state.editGroups;
                const {
                    features: { isZeroSpeedPageForGeoproductEnabled }
                } = state.commonData;

                const groupEditData = groups[groupId];

                const additiveActions = [];

                if (isZeroSpeedPageForGeoproductEnabled && group.groupType === GroupType.CPM_GEOPRODUCT) {
                    const defaultTargetTagsEditors = getTargetTagsEditorsState([groupEditData], productsList, [
                        TargetPlatformTags.APP_NAVI,
                        TargetPlatformTags.APP_METRO
                    ]);

                    additiveActions.push(initTargetTagsEditors(defaultTargetTagsEditors));
                }

                if (isZeroSpeedPageForGeoproductEnabled && group.groupType !== GroupType.CPM_GEOPRODUCT) {
                    const editorId = GroupsHelpers.getGroupEditPageItemEditorId(groupId);
                    additiveActions.push(resetTargetTagsEditors([editorId]));
                }

                return [
                    closeRateCorrectionsEditor(groupId),
                    updateProductRestrictionIdAction(groupId, group.restrictionId),
                    clearRateCorrectionsEditor(groupId),
                    ...additiveActions
                ];
            }
        )
    );
};

// FIXME: Оторвать внутренние группы от внешних
//        @alex-sannikov
//        https://st.yandex-team.ru/DIRECT-100081
const getInternalGroupsForEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_INTERNAL_GROUPS_FOR_EDIT),
        mergeMap((action: GetInternalGroupsForEditAction) => {
            const state = store$.value;
            const { groupsData } = action;
            const {
                commonData: { client, regions, stringDicts }
            } = state;

            const regionTree: Region[] = regions
                ? RegionsHelpers.getRegionsTree(regions, LangUtils.getCurrentLang(), client.translocalGeotreeType)
                : [];

            const groupsForEditData = mapGroupsForEditQueryResultToQueryResponse(
                action.login,
                groupsData,
                regionTree,
                false
            );

            return [
                ...initGroupsEditors(groupsForEditData as DataForEditInternalGroups, stringDicts),
                updateDataForEditGroups(groupsForEditData)
            ];
        })
    );
};

const getCpcGroupsForEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_CPC_GROUPS_FOR_EDIT),
        mergeMap((action: GetCpcGroupsForEditAction) => {
            const state = store$.value;
            const { groupsData } = action;
            const { isCpcDeviceModifiersAllowed } = selectClientFeatures(state);
            const {
                commonData: {
                    client,
                    regions,
                    login,
                    features: { isTextBannerInterestsRetCondEnabled },
                    operatorFeatures: { isTargetTagsAllowed }
                }
            } = state;
            const {
                client: { interests }
            } = groupsData.data;
            const regionTree: Region[] = regions
                ? RegionsHelpers.getRegionsTree(regions, LangUtils.getCurrentLang(), client.translocalGeotreeType)
                : [];

            return from(
                deps.groupsDataProvider.getRetargetingConditionsForUserProfile(
                    login,
                    interests.rowset.map(interest => interest.retargetingConditionId)
                )
            ).pipe(
                mergeMap(userProfileRetargetings => {
                    const groupsForEditData = mapGroupsForEditQueryResultToQueryResponse(
                        action.login,
                        groupsData,
                        regionTree,
                        Boolean(isCpcDeviceModifiersAllowed),
                        userProfileRetargetings,
                        undefined,
                        isTargetTagsAllowed
                    );
                    const isFetchCrypta = GroupsHelpers.getFetchCryptaFlags(
                        groupsForEditData,
                        isTextBannerInterestsRetCondEnabled
                    );

                    if (isFetchCrypta) {
                        return from(
                            deps.groupsDataProvider.getCryptaSegments({
                                name: '',
                                type: [WebGoalTypeInput.interests]
                            })
                        ).pipe(
                            mergeMap(cryptaSegmentsResult => {
                                return [
                                    updateCryptaSegments(mapCryptaSegmentsFromApiFormat(cryptaSegmentsResult)),
                                    updateDataForEditGroups(groupsForEditData)
                                ];
                            })
                        );
                    } else {
                        return [updateDataForEditGroups(groupsForEditData)];
                    }
                }),
                catchError((error: ApiMethodExecutingError) => {
                    return of(showGroupEditError(error));
                })
            );
        })
    );
};

const getCpmGroupsForEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    const { login } = store$.value.commonData;

    return action$.pipe(
        ofType(GET_CPM_GROUPS_FOR_EDIT),
        mergeMap(
            (action: GetCpmGroupsForEditAction): Observable<Action> => {
                const state = store$.value;
                const { groupsData } = action;

                const {
                    client: {
                        interests,
                        adGroups: { rowset }
                    }
                } = groupsData.data;
                const {
                    editGroups: { groupsIds, campaignsIds },
                    commonData: {
                        client: { workCurrency }
                    }
                } = state;

                if (groupsIds.length === 0 || campaignsIds.length === 0) {
                    const url = UrlHelpers.getGroupsListUrl();
                    return of(
                        changeLocation(url, {
                            ulogin: login
                        })
                    );
                }

                const isCPMPriceCampaign = rowset.some(
                    item => item.campaign && item.campaign.type === GdCampaignType.CPM_PRICE
                );
                const isCPMBannerCampaign = rowset.some(
                    item => item.campaign && item.campaign.type === GdCampaignType.CPM_BANNER
                );
                let campaignIdsWithSpecificGroup: string[] = [];

                if (isCPMPriceCampaign) {
                    campaignIdsWithSpecificGroup = rowset
                        .filter(item => {
                            if (item.campaign.type === GdCampaignType.CPM_PRICE) {
                                const priority = get(item, 'priority');

                                return String(priority) === GROUP_PRIORITY.SPECIFIC;
                            }

                            return false;
                        })
                        .map(item => item.campaign.id);
                }

                const emptyResponse = Promise.resolve(undefined);

                return forkJoin(
                    isCPMBannerCampaign
                        ? deps.groupsDataProvider.getProducts({
                              calcType: CampaignsCalcTypeFilter.CPM,
                              type: CampaignType.CPM_BANNER,
                              currency: workCurrency
                          })
                        : emptyResponse,
                    isCPMPriceCampaign
                        ? deps.groupsDataProvider.getProducts({
                              calcType: CampaignsCalcTypeFilter.CPM,
                              type: CampaignType.CPM_PRICE,
                              currency: workCurrency
                          })
                        : emptyResponse,
                    deps.groupsDataProvider.getRetargetingConditionsForUserProfile(
                        login,
                        interests.rowset.map(ret => ret.retargetingConditionId)
                    ),
                    campaignIdsWithSpecificGroup && campaignIdsWithSpecificGroup.length
                        ? deps.groupsDataProvider.getGroupEditRegionTree(login, campaignIdsWithSpecificGroup)
                        : emptyResponse
                ).pipe(
                    mergeMap(
                        ([productsListBanner = {}, productsListPrice = {}, userProfileRetargetings, regionTrees]) => {
                            const productsList = Object.assign({}, productsListBanner, productsListPrice);
                            const {
                                commonData: {
                                    client,
                                    regions,
                                    operatorFeatures: { isTargetTagsAllowed },
                                    features: { isZeroSpeedPageForGeoproductEnabled }
                                }
                            } = state;

                            const regionTree: Region[] = regions
                                ? RegionsHelpers.getRegionsTree(
                                      regions,
                                      LangUtils.getCurrentLang(),
                                      client.translocalGeotreeType
                                  )
                                : [];

                            const groupsForEditData = mapGroupsForEditQueryResultToQueryResponse(
                                action.login,
                                groupsData,
                                regionTree,
                                true,
                                userProfileRetargetings,
                                productsList,
                                isTargetTagsAllowed,
                                regionTrees
                            );

                            const {
                                fetchIndoorPlacements,
                                fetchOutdoorPlacements,
                                fetchCrypta
                            } = GroupsHelpers.getFetchFlags(groupsForEditData, productsList);

                            return forkJoin(
                                fetchOutdoorPlacements
                                    ? deps.groupsDataProvider.getPlacements({
                                          placementType: PlacementTypeInput.OUTDOOR
                                      })
                                    : emptyResponse,
                                fetchIndoorPlacements
                                    ? deps.groupsDataProvider.getPlacements({
                                          placementType: PlacementTypeInput.INDOOR
                                      })
                                    : emptyResponse,
                                fetchCrypta
                                    ? deps.groupsDataProvider.getCryptaSegments({
                                          name: ''
                                      })
                                    : emptyResponse
                            ).pipe(
                                mergeMap(([placementsOutdoor, placementsIndoor, cryptaSegmentsResult]) => {
                                    const additiveActions = [];

                                    if (placementsOutdoor) {
                                        additiveActions.push(
                                            updateOutdoorMapData(
                                                mapOutdoorPlacementsMapFromApiFormat(placementsOutdoor)
                                            )
                                        );
                                    }

                                    if (placementsIndoor) {
                                        additiveActions.push(
                                            updateIndoorMapData(mapIndoorPlacementsMapFromApiFormat(placementsIndoor))
                                        );
                                    }

                                    if (cryptaSegmentsResult) {
                                        additiveActions.push(
                                            updateCryptaSegments(mapCryptaSegmentsFromApiFormat(cryptaSegmentsResult))
                                        );
                                    }

                                    const groupListValues = values(groupsForEditData.groups);

                                    const groupSupportsRecommendationIds = groupListValues
                                        .filter(value =>
                                            isGroupSupportsRecommendationsOnEditBannerPage(value.value.groupType)
                                        )
                                        .map(group => group.id);

                                    const getRecommendationAction = groupSupportsRecommendationIds.length
                                        ? [
                                              fetchRecommendationsForGroupsEdit(
                                                  login,
                                                  campaignsIds,
                                                  groupSupportsRecommendationIds
                                              )
                                          ]
                                        : [];

                                    if (isZeroSpeedPageForGeoproductEnabled) {
                                        const geoGroupsValues = groupListValues.filter(
                                            group => group.value.groupType === GroupType.CPM_GEOPRODUCT
                                        );
                                        const targetTagsEditorStates = getTargetTagsEditorsState(
                                            geoGroupsValues,
                                            productsList
                                        );
                                        additiveActions.push(initTargetTagsEditors(targetTagsEditorStates));
                                    }
                                    return [
                                        ...additiveActions,
                                        ...getRecommendationAction,
                                        updateProductsList(productsList),
                                        updateDataForEditGroups(groupsForEditData)
                                    ];
                                })
                            );
                        }
                    ),
                    catchError((error: ApiMethodExecutingError) => {
                        return of(showGroupEditError(error));
                    })
                );
            }
        )
    );
};

const getRecommendationsForGroupsEditEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_RECOMMENDATIONS_FOR_GROUPS_EDIT),
        mergeMap(
            (action: FetchRecommendationsForGroupsEditAction): Observable<Action> => {
                return from(
                    deps.groupsDataProvider.getGroupEditRecommendations(
                        action.login,
                        action.campainIds,
                        action.groupIds
                    )
                ).pipe(
                    map(
                        (data): Action => {
                            return updateRecommendationsForGroupsEdit(mapGroupEditRecommendationsToQueryResponse(data));
                        }
                    ),
                    catchError(
                        (error: ApiMethodExecutingError): Observable<Action> => {
                            return of(showGroupEditError(error));
                        }
                    )
                );
            }
        )
    );
};

const openRecommendationsModalForGroupsEditEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(OPEN_RECOMMENDATIONS_MODAL_FOR_GROUPS_EDIT),
        map(
            (action: OpenRecommendationsModalForGroupsEditAction): Action => {
                const state = store$.value;
                const modalData: RecommendationsModalState = getGroupModalRecommendations(state, action.id);

                return setRecommendationsModalDataAction(modalData);
            }
        )
    );
};

const getRetargetingConditionsForGroupsEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT),
        awaitCommonData(action$, store$),
        mergeMap(
            (
                action: GetRetargetingConditionsForGroupsEditAction
            ): Observable<UpdateRetargetingConditionsForEditGroupsAction> => {
                return from(deps.groupsDataProvider.getRetargetingConditions(action.login)).pipe(
                    mergeMap(retargetingResult => {
                        const retargetingConditionsForGroupsEdit = mapRetargetingConditionsQueryResultToQueryResponse(
                            action.login,
                            retargetingResult
                        );

                        return [updateRetargetingConditionsForEditGroups(retargetingConditionsForGroupsEdit)];
                    })
                );
            }
        )
    );
};

const saveGroupsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    const processResponse = (
        response: FetchResult<SaveAdGroup>,
        groupsData: Record<GroupId, EditGroupData>,
        groupsCategoryGroupIdsMap: Record<GroupsCategories, GroupId[]>,
        withRedirect: boolean,
        thenCreateBanners: boolean,
        campaignId: string
    ) => {
        const {
            commonData: { login, fromOldInterface }
        } = store$.value;
        let resultActions: Array<Action> = [];

        let groupsDataToSave = groupsData;

        if (response.errors && response.errors.length) {
            resultActions = [showGroupEditError(response.errors[0])];
        } else {
            const savedGroupsIds = extractSavedGroupIds(response.data);

            const validationErrors = getSaveGroupsValidationErrors(response, groupsCategoryGroupIdsMap);

            const hasErrors = !isEmpty(validationErrors);

            if (!hasErrors && withRedirect) {
                let changeLocationAction: Action;

                if (thenCreateBanners) {
                    changeLocationAction = goToBannersEditForCreation(login, campaignId, savedGroupsIds[0]);
                } else {
                    if (fromOldInterface) {
                        changeLocationAction = changeLocationHard(UrlHelpers.getShowCampaignUrl(campaignId, login));
                    } else {
                        changeLocationAction = changeLocation(UrlHelpers.getGroupsListUrl(), {
                            ulogin: login,
                            [GridScreenUrlSearchQueryParams.calcType]: CampaignsCalcTypeFilter.CPC
                        });
                    }
                }

                resultActions = [changeLocationAction];
            }

            if (!withRedirect) {
                // в случае, если экшен затребован без редиректа (при создании смарт-группы)
                // нужно обновить serverValue, чтобы не было "несохраненных изменений"
                groupsDataToSave = GroupsHelpers.markEditGroupsAsSaved(groupsDataToSave, savedGroupsIds);
            }

            if (hasErrors) {
                const editorsResultActions$ = getShowEditorsErrorsFromServerActions(validationErrors);

                resultActions = [
                    showGroupsPartialSaveResult({
                        errors: validationErrors,
                        updatedGroupIds: savedGroupsIds
                    }),
                    ...editorsResultActions$
                ];
            }
        }

        return from([
            updateDataForEditGroups(
                {
                    groups: groupsDataToSave
                },
                true
            ),
            ...resultActions
        ]);
    };

    return action$.pipe(
        ofType(SAVE_GROUPS),
        filter(() => {
            const {
                editGroups: { groups }
            } = store$.value;
            return some(
                groups,
                ({ value: { campaignType } }) =>
                    campaignType !== CampaignType.CPM_BANNER && campaignType !== CampaignType.CPM_PRICE
            );
        }),
        mergeMap(
            (action: SaveGroupsAction): Observable<Action> => {
                const state = store$.value;
                const cryptaSegmentsByType = getAllCryptaSegmentsByType(state);
                const {
                    editGroups: { groups, isNewEmptyGroup, campaignsIds }
                } = state;
                const accessRights = selectAccessRights(state);
                const clientFeatures = selectClientFeatures(state);
                const operatorFeatures = selectOperatorFeatures(state);
                const editableTypes = selectEditableGroupTypes({ operatorFeatures, accessRights, clientFeatures });
                const campaignId = campaignsIds[0];
                const {
                    login,
                    regions,
                    client: { workCurrency, translocalGeotreeType },
                    constants
                } = state.commonData;
                const regionTree: Region[] = regions
                    ? RegionsHelpers.getRegionsTree(regions, LangUtils.getCurrentLang(), translocalGeotreeType)
                    : [];

                const groupsData = getGroupsValueFromOpenedEditors(
                    groups,
                    state.rateCorrectionsEditor,
                    state.regionsEditor,
                    state.retargetingEditor,
                    state.keywordsEditor.editors,
                    state.minusPhraseLibraryEditor,
                    state.editPageItemNameEditor,
                    state.audienceTargetingsEditor,
                    state.interestsTargetingEditor,
                    state.cryptaSocialDemoEditor,
                    state.cryptaGroupFamilyEditor,
                    state.cryptaAudioGenresEditor,
                    state.cryptaBehavioralEditor,
                    state.retargetingAudienceEditor,
                    state.piTagsEditor,
                    state.targetTagsEditor,
                    regionTree,
                    editableTypes,
                    clientFeatures
                );

                if (campaignId === undefined) {
                    throw new Error('не задан номер кампании');
                }

                if (constants) {
                    const openedEditorsValidationResult = getGroupsOpenedEditorsValidationResult(
                        values(groupsData),
                        state
                    );

                    const clientValidationErrors = new ExternalGroupValidationSchema().validate(values(groupsData), {
                        minGeneralPrice: constants.currencyConstants[workCurrency].minPrice,
                        maxGeneralPrice: constants.currencyConstants[workCurrency].maxPrice,
                        currency: workCurrency
                    });

                    let isValid = true;
                    let showErrorsActions: Array<Observable<Action>> = [];

                    if (Object.keys(clientValidationErrors).length !== 0) {
                        isValid = false;

                        showErrorsActions.push(Observable.of(showGroupsClientValidationErrors(clientValidationErrors)));
                    }

                    if (openedEditorsValidationResult.valid === false) {
                        isValid = false;

                        showErrorsActions = [...showErrorsActions, ...openedEditorsValidationResult.showErrorsActions];
                    }

                    if (isValid === false) {
                        return Observable.concat(
                            Observable.of(changeGroupEditPageStatus(PageStatus.LOADED)),
                            ...showErrorsActions
                        );
                    }
                }

                const saveGroupsInput = mapGroupsToSaveGroupsInput(
                    groupsData,
                    cryptaSegmentsByType,
                    campaignId,
                    clientFeatures
                );
                const groupsCategoryGroupIdsMap = getCategoryGroupIdsMap(groupsData, clientFeatures);

                return from(deps.groupsMutationExecutor.saveGroups(saveGroupsInput, login)).pipe(
                    mergeMap(response =>
                        processResponse(
                            response,
                            groupsData,
                            groupsCategoryGroupIdsMap,
                            action.withRedirect,
                            isNewEmptyGroup,
                            campaignId
                        )
                    ),
                    catchError(
                        (
                            error: ApiMethodExecutingError | NotSyncedError<FetchResult<SaveAdGroup>>
                        ): Observable<Action> => {
                            if (error instanceof NotSyncedError) {
                                // для NotSynced обрабатываем результат, так же как для обычного ответа
                                return processResponse(
                                    error.data,
                                    groupsData,
                                    groupsCategoryGroupIdsMap,
                                    false,
                                    isNewEmptyGroup,
                                    campaignId
                                );
                            }
                            return of(showGroupEditError(error));
                        }
                    )
                );
            }
        )
    );
};

const saveCpmGroupsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    const processCpmResponse = (
        response: FetchResult<UpdateCpmAdGroup & { notSynced?: boolean }>,
        groupsData: Record<GroupId, EditGroupData>,
        cpmGroupIds: Array<GroupId>,
        withRedirect: boolean,
        thenCreateBanners: boolean,
        campaignId: string
    ): Observable<Action> => {
        const {
            commonData: { login, fromOldInterface }
        } = store$.value;
        let resultActions: Array<Action> = [];

        let groupsDataToSave = groupsData;

        if (response.errors && response.errors.length) {
            resultActions = [showGroupEditError(response.errors[0])];
        } else {
            if (!response.data) {
                throw new Error('internal server error');
            }

            const { saveCpmAdGroups } = response.data;

            const [hasCpmValidationErrors, cpmServerErrors] = extractValidationErrors(saveCpmAdGroups);

            // эту функцию я упер с сайта lodash
            // она нужна, чтобы при слиянии двух объектов массивы тоже сливались
            // без кастомайзера массивы перезаписывают друг друга
            const customizer = (objValue: Object, srcValue: Object): Object | undefined => {
                if (isArray(objValue)) {
                    return objValue.concat(srcValue);
                }
                return undefined;
            };

            let validationErrors: ErrorsEdit = {};

            if (hasCpmValidationErrors) {
                const cpmGroupUpdateHandleErrorMapper = createCpmGroupUpdateHandleErrorMapper(cpmGroupIds);
                const cpmValidationErrors = cpmGroupUpdateHandleErrorMapper.mapServerErrorsToClientError(
                    cpmServerErrors
                );

                validationErrors = mergeWith(validationErrors, cpmValidationErrors, customizer);
            }

            if (!hasCpmValidationErrors && withRedirect) {
                let changeLocationAction: Action;

                if (thenCreateBanners) {
                    const groupId = get(saveCpmAdGroups, 'updatedAdGroupItems[0].adGroupId', cpmGroupIds[0]);
                    changeLocationAction = goToBannersEditForCreation(login, campaignId, String(groupId));
                } else {
                    if (fromOldInterface) {
                        changeLocationAction = changeLocationHard(UrlHelpers.getShowCampaignUrl(campaignId, login));
                    } else {
                        changeLocationAction = changeLocation(UrlHelpers.getGroupsListUrl(), {
                            ulogin: login,
                            [GridScreenUrlSearchQueryParams.calcType]: CampaignsCalcTypeFilter.CPM
                        });
                    }
                }

                resultActions = [changeLocationAction];
            }

            const updatedGroupIds: GroupId[] = extractUpdatedGroupIds(saveCpmAdGroups);

            if (!withRedirect) {
                // в случае, если экшен затребован без редиректа (при создании смарт-группы)
                // нужно обновить serverValue, чтобы не было "несохраненных изменений"
                groupsDataToSave = GroupsHelpers.markEditGroupsAsSaved(groupsDataToSave, updatedGroupIds);
            }

            if (hasCpmValidationErrors) {
                const editorsResultActions$ = getShowEditorsErrorsFromServerActions(validationErrors);

                resultActions = [
                    showGroupsPartialSaveResult({
                        errors: validationErrors,
                        updatedGroupIds
                    }),
                    ...editorsResultActions$
                ];
            }
        }

        return from([
            updateDataForEditGroups(
                {
                    groups: groupsDataToSave
                },
                true
            ),
            ...resultActions
        ]);
    };

    return action$.pipe(
        ofType(SAVE_GROUPS),
        filter(() => {
            const {
                editGroups: { groups }
            } = store$.value;
            return some(
                groups,
                ({ value: { campaignType } }) =>
                    campaignType === CampaignType.CPM_BANNER || campaignType === CampaignType.CPM_PRICE
            );
        }),
        mergeMap(
            (action: SaveGroupsAction): Observable<Action> => {
                const state = store$.value;
                const { editGroups } = state;
                const { groups, isNewEmptyGroup, campaignsIds, productsList } = editGroups;
                const campaignId = campaignsIds[0];
                const {
                    login,
                    regions,
                    client: { workCurrency, translocalGeotreeType },
                    features,
                    constants
                } = state.commonData;
                const accessRights = selectAccessRights(state);
                const operatorFeatures = selectOperatorFeatures(state);
                const editableTypes = selectEditableGroupTypes({ operatorFeatures, accessRights });
                const cryptaSegmentsByType = getAllCryptaSegmentsByType(state);
                const regionTree: Region[] = regions
                    ? RegionsHelpers.getRegionsTree(regions, LangUtils.getCurrentLang(), translocalGeotreeType)
                    : [];
                const groupsData = getGroupsValueFromOpenedEditors(
                    groups,
                    state.rateCorrectionsEditor,
                    state.regionsEditor,
                    state.retargetingEditor,
                    state.keywordsEditor.editors,
                    state.minusPhraseLibraryEditor,
                    state.editPageItemNameEditor,
                    state.audienceTargetingsEditor,
                    state.interestsTargetingEditor,
                    state.cryptaSocialDemoEditor,
                    state.cryptaGroupFamilyEditor,
                    state.cryptaAudioGenresEditor,
                    state.cryptaBehavioralEditor,
                    state.retargetingAudienceEditor,
                    state.piTagsEditor,
                    state.targetTagsEditor,
                    regionTree,
                    editableTypes,
                    features
                );

                if (campaignId === undefined) {
                    throw new Error('не задан номер кампании');
                }

                if (productsList === undefined) {
                    throw new Error('не получен список продуктов');
                }

                const [cpmInput, cpmGroupsIds] = mapCpmGroupsDataToApiFormat(
                    groupsData,
                    productsList,
                    cryptaSegmentsByType
                );

                if (constants) {
                    const openedEditorsValidationResult = getGroupsOpenedEditorsValidationResult(
                        values(groupsData),
                        state
                    );

                    const clientValidationErrors = new ExternalGroupValidationSchema().validate(values(groupsData), {
                        minGeneralPrice: constants.currencyConstants[workCurrency].minCpmPrice,
                        maxGeneralPrice: constants.currencyConstants[workCurrency].maxCpmPrice,
                        currency: workCurrency
                    });
                    let isValid = true;
                    let showErrorsActions: Array<Observable<Action>> = [];

                    if (Object.keys(clientValidationErrors).length !== 0) {
                        isValid = false;

                        showErrorsActions.push(Observable.of(showGroupsClientValidationErrors(clientValidationErrors)));
                    }

                    if (openedEditorsValidationResult.valid === false) {
                        isValid = false;

                        showErrorsActions = [...showErrorsActions, ...openedEditorsValidationResult.showErrorsActions];
                    }

                    if (isValid === false) {
                        return Observable.concat(
                            Observable.of(changeGroupEditPageStatus(PageStatus.LOADED)),
                            ...showErrorsActions
                        );
                    }
                }

                if (isUserProfileEmpty(groupsData, cryptaSegmentsByType) && features.isEmptyRetCondInCpmGroupEnabled) {
                    return from(
                        confirm({
                            message: i18n('warning-about-empty-retargeting-conditions')
                        })
                    ).pipe(
                        mergeMap(() => {
                            return Observable.fromPromise(deps.groupsMutationExecutor.updateCpmGroups(cpmInput, login))
                                .mergeMap(response =>
                                    processCpmResponse(
                                        response,
                                        groupsData,
                                        cpmGroupsIds,
                                        action.withRedirect,
                                        isNewEmptyGroup,
                                        campaignId
                                    )
                                )
                                .catch(
                                    (
                                        error: ApiMethodExecutingError | NotSyncedError<FetchResult<UpdateCpmAdGroup>>
                                    ): Observable<Action> => {
                                        if (error instanceof NotSyncedError) {
                                            // для NotSynced обрабатываем результат, так же как для обычного ответа
                                            return processCpmResponse(
                                                error.data,
                                                groupsData,
                                                cpmGroupsIds,
                                                false,
                                                isNewEmptyGroup,
                                                campaignId
                                            );
                                        }
                                        return of(showGroupEditError(error));
                                    }
                                );
                        }),
                        catchError((): Observable<Action> => of(changeGroupEditPageStatus(PageStatus.LOADED)))
                    );
                }

                return Observable.fromPromise(deps.groupsMutationExecutor.updateCpmGroups(cpmInput, login))
                    .mergeMap(response =>
                        processCpmResponse(
                            response,
                            groupsData,
                            cpmGroupsIds,
                            action.withRedirect,
                            isNewEmptyGroup,
                            campaignId
                        )
                    )
                    .catch(
                        (
                            error: ApiMethodExecutingError | NotSyncedError<FetchResult<UpdateCpmAdGroup>>
                        ): Observable<Action> => {
                            if (error instanceof NotSyncedError) {
                                // для NotSynced обрабатываем результат, так же как для обычного ответа
                                return processCpmResponse(
                                    error.data,
                                    groupsData,
                                    cpmGroupsIds,
                                    false,
                                    isNewEmptyGroup,
                                    campaignId
                                );
                            }
                            return of(showGroupEditError(error));
                        }
                    );
            }
        )
    );
};

export const getShowEditorsErrorsFromServerActions = (errorsInfo: ErrorsEdit): Action[] => {
    const resultActions: Action[] = [];

    if (errorsInfo.groups) {
        forEach(errorsInfo.groups, (groupErrors, groupId) => {
            if (groupErrors.name && groupErrors.name.errors) {
                resultActions.push(
                    showEditPageItemNameEditorErrors(
                        GroupsHelpers.getGroupEditPageItemEditorId(groupId),
                        groupErrors.name.errors
                    )
                );
            }

            const minusKeywordsErrors = groupErrors.minusKeywords && groupErrors.minusKeywords.errors;
            const libraryMinusKeywordsIdsErrors =
                groupErrors.libraryMinusKeywordsIds && groupErrors.libraryMinusKeywordsIds.errors;

            if (minusKeywordsErrors || libraryMinusKeywordsIdsErrors) {
                resultActions.push(
                    showEditPageMinusPhrasesEditorErrors(
                        groupId,
                        minusKeywordsErrors || [],
                        libraryMinusKeywordsIdsErrors || []
                    )
                );
            }

            if (groupErrors.keywords && groupErrors.keywords.errors) {
                resultActions.push(
                    keywordsEditorSetErrors(getGroupKeywordsEditorId(groupId), groupErrors.keywords.errors)
                );
            }
        });
    }

    return resultActions;
};

export const updateGroupNameEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_GROUP_NAME_EDITOR_CHANGES),
        switchMap((action: SaveGroupNameEditorChangesAction): Action[] => {
            const { groupId } = action;
            const state = store$.value;
            const { editPageItemNameEditor } = state;
            const editorId = GroupsHelpers.getGroupEditPageItemEditorId(groupId);
            const editorData = selectEditPageItemNameEditorValue(editPageItemNameEditor, editorId);

            const validationErrors = getGroupNameEditorValidationResult(state, groupId);

            if (validationErrors.length === 0) {
                return [changeGroupName(groupId, editorData.value), closeEditPageItemNameEditor(editorId)];
            }

            return [showEditPageItemNameEditorErrors(editorId, validationErrors)];
        })
    );
};

const openGroupRetargetingEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store, deps) => {
    return action$.ofType(OPEN_GROUP_RETARGETING_EDITOR).mergeMap((action: OpenGroupRetargetingEditor) => {
        const { editGroups } = store.value;
        const groupValue = editGroups.groups[action.groupId].value;

        if (groupValue.campaignType !== CampaignType.TEXT) {
            throw Error(`В кампании с типом ${groupValue.campaignType} нет условий ретаргетинга`);
        }

        if (editGroups.retargetingConditions === undefined) {
            return EMPTY;
        }

        return of(
            openRetargetingEditor(
                action.groupId,
                editGroups.retargetingConditions.filter(
                    retargetingCondition => !retargetingCondition.isForRatesAdjustment
                ),
                groupValue.retargeting.map(retargetingItem => retargetingItem.id)
            )
        );
    });
};

const saveGroupRetargetingEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store, deps) => {
    return action$.ofType(SAVE_GROUP_RETARGETING_EDITOR).mergeMap((action: SaveGroupRetargetingEditor) => {
        const {
            retargetingEditor,
            editGroups: { groups }
        } = store.value;
        const groupValue = groups[action.groupId].value;

        if (groupValue.campaignType !== CampaignType.TEXT) {
            throw Error(`В кампании с типом ${groupValue.campaignType} нет условий ретаргетинга`);
        }

        return Observable.merge(
            Observable.of(
                changeGroupRetargeting(
                    action.groupId,
                    mapRetargetingEditorValueToGroupRetargetingValue(
                        retargetingEditor[action.groupId].conditions,
                        groupValue.retargeting
                    )
                )
            ),
            Observable.of(closeRetargetingEditor(action.groupId))
        );
    });
};

const updateGroupRetargetingEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store, deps) => {
    return action$
        .ofType(CHANGE_RETARGETING_LIST_ITEM_SELECTION, CHANGE_RETARGETING_LIST_SELECTION, DELETE_RETARGETING_LIST_ITEM)
        .map(
            (action: ChangeRetargetingListItemSelection | ChangeRetargetingListSelection | DeleteRetargetingListItem) =>
                resetRetargetingErrors(action.id)
        );
};

const openPhrasesSelectorWizardForGroupsEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT),
        mergeMap(
            (action: OpenPhrasesSelectorWizardForGroupsEdit): DnaEpicAction => {
                const state = store$.value;
                const group = state.editGroups.groups[action.groupId];
                const { id, campaign, value } = group;
                const groupDataValue = getEditGroupDataValueWithRegions(value);

                if (!groupDataValue) {
                    return EMPTY;
                }

                const currentKeywords = selectEditGroupCurrentKeywords(state, action.groupId);
                const minusKeywords = selectEditGroupCurrentMinusPhrases(state, action.groupId);
                const minusKeywordsLibraryList = selectEditGroupCurrentMinusPhraseLibraries(state, action.groupId);

                const {
                    name,
                    regions: { regionIds },
                    groupType
                } = groupDataValue;

                const startPhrases = currentKeywords.map(item =>
                    mapPhraseToPhrasesSelectorListItem({
                        shows: 0,
                        phrase: item.phrase
                    })
                );

                return [
                    openPhrasesSelectorWizard(PhrasesSelectorWizardId.editGroupsPage, {
                        id,
                        name,
                        groupType,
                        campaign: {
                            ...campaign,
                            type: value.campaignType
                        },
                        minusKeywords,
                        minusKeywordsLibraryList,
                        regionIds,
                        startPhrases
                    }),
                    addPhrasesStartInputItems(
                        PhrasesSelectorWizardId.editGroupsPage,
                        currentKeywords.map(item => item.phrase)
                    )
                ];
            }
        )
    );
};

const openEditGroupPhrasesSelectorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_EDIT_GROUP_PHRASES_SELECTOR_BETA),
        mergeMap(
            (action: OpenPhrasesSelectorWizardForGroupsEdit): DnaEpicAction => {
                const state = store$.value;
                const group = state.editGroups.groups[action.groupId];
                const { id, campaign, value } = group;
                const groupDataValue = getEditGroupDataValueWithRegions(value);

                if (!groupDataValue) {
                    return EMPTY;
                }
                const currentKeywords = selectEditGroupCurrentKeywords(state, action.groupId);
                const minusKeywords = selectEditGroupCurrentMinusPhrases(state, action.groupId);
                const minusKeywordsLibraryList = selectEditGroupCurrentMinusPhraseLibraries(state, action.groupId);

                const {
                    name,
                    regions: { regionIds },
                    groupType
                } = groupDataValue;

                const startPhrases = currentKeywords.map(item =>
                    mapPhraseToPhrasesSelectorListItem({
                        shows: 0,
                        phrase: item.phrase
                    })
                );

                const keywords = currentKeywords.map(item => item.phrase);
                const openPhrasesSelector = phrasesSelectorOpenEditor(PhrasesSelectorWizardId.editGroupsPage, {
                    id,
                    name,
                    groupType,
                    campaign: {
                        ...campaign,
                        type: value.campaignType
                    },
                    minusKeywords,
                    minusKeywordsLibraryList,
                    regionIds,
                    keywords: {},
                    startPhrases
                });

                if (keywords.length === 0) {
                    return of(openPhrasesSelector);
                }

                return [
                    openPhrasesSelector,
                    phrasesSelectorSetGroupKeywords(PhrasesSelectorWizardId.editGroupsPage, keywords),
                    phrasesSelectorGetStats(PhrasesSelectorWizardId.editGroupsPage, keywords)
                ];
            }
        )
    );
};

const applySelectorWizardForGroupsEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT),
        mergeMap(
            (action: ApplySelectorWizardForGroupEdit): DnaEpicAction => {
                const state = store$.value;
                const { groupId, minusPhrases, listChosenKeywords } = action;

                const groupDataValue = selectEditGroupValueInfo(state, groupId);
                const keywordsEditorId = getGroupKeywordsEditorId(groupId);
                const keywordsEditor = selectKeywordsEditorData(state, keywordsEditorId);
                const minusKeywordsEditor = selectMinusPhrasesEditorById(state, groupId).minusKeywordsEditor;

                const groupDataValueWithKeywords = getEditGroupDataValueWithKeywords(groupDataValue);
                const groupDataValueWithMinusPhrases = getEditGroupDataValueWithMinusPhrases(groupDataValue);

                if (!groupDataValueWithKeywords) {
                    return EMPTY;
                }

                const actions: Action[] = [closePhrasesSelectorWizard(PhrasesSelectorWizardId.editGroupsPage)];

                if (keywordsEditor && keywordsEditor.isOpen) {
                    actions.push(keywordsEditorUpdate(keywordsEditorId, listChosenKeywords));
                } else {
                    const newPhrases = parsePhrasesFromStringList(listChosenKeywords);
                    const newListKeywords = getUniqJoinedKeywordsData(
                        groupDataValueWithKeywords.keywords || [],
                        newPhrases
                    );

                    actions.push(groupDataUpdateKeywords(action.groupId, newListKeywords));
                }

                if (minusKeywordsEditor && minusKeywordsEditor.isOpen) {
                    const existingMinusWords = minusKeywordsEditor.value;

                    actions.push(changeMinusPhrasesEditor(groupId, existingMinusWords.concat(action.minusPhrases)));
                } else if (groupDataValueWithMinusPhrases) {
                    const newMinusPhrases = uniq(groupDataValueWithMinusPhrases.minusKeywords.concat(minusPhrases));

                    actions.push(updateGroupMinusPhrases(groupId, newMinusPhrases));
                }

                return actions;
            }
        )
    );
};

const savePhrasesSelectorPhrasesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(SAVE_PHRASES_SELECTOR_PHRASES),
        mergeMap(
            (action: SavePhrasesSelectorPhrasesAction): DnaEpicAction => {
                const state = store$.value;
                const { groupId, keywords, minusPhrases } = action;

                const groupDataValue = selectEditGroupValueInfo(state, groupId);
                const keywordsEditorId = getGroupKeywordsEditorId(groupId);
                const keywordsEditor = selectKeywordsEditorData(state, keywordsEditorId);
                const minusKeywordsEditor = selectMinusPhrasesEditorById(state, groupId).minusKeywordsEditor;

                const groupDataValueWithKeywords = getEditGroupDataValueWithKeywords(groupDataValue);
                const groupDataValueWithMinusPhrases = getEditGroupDataValueWithMinusPhrases(groupDataValue);

                if (!groupDataValueWithKeywords) {
                    return EMPTY;
                }

                const actions: Action[] = [
                    phrasesSelectorHideEditor(PhrasesSelectorWizardId.editGroupsPage),
                    phrasesSelectorClearData(action.groupId)
                ];

                if (keywordsEditor && keywordsEditor.isOpen) {
                    actions.push(keywordsEditorUpdate(keywordsEditorId, keywords));
                } else {
                    const newPhrases = parsePhrasesFromStringList(keywords);
                    const newListKeywords = getUniqJoinedKeywordsData(
                        groupDataValueWithKeywords.keywords || [],
                        newPhrases
                    );

                    actions.push(groupDataUpdateKeywords(action.groupId, newListKeywords));
                }

                if (minusKeywordsEditor && minusKeywordsEditor.isOpen) {
                    const existingMinusWords = minusKeywordsEditor.value;

                    actions.push(changeMinusPhrasesEditor(groupId, existingMinusWords.concat(minusPhrases)));
                } else if (groupDataValueWithMinusPhrases) {
                    const newMinusPhrases = uniq(groupDataValueWithMinusPhrases.minusKeywords.concat(minusPhrases));

                    actions.push(updateGroupMinusPhrases(groupId, newMinusPhrases));
                }

                return actions;
            }
        )
    );
};

const saveGroupRateCorrectionEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_GROUP_RATE_CORRECTIONS_EDITOR),
        mergeMap(
            (action: SaveGroupRateCorrectionsEditorAction): DnaEpicAction => {
                const state = store$.value;
                const groupEditorState = state.rateCorrectionsEditor[action.id].value;

                const validationResult = validateRateCorrectionEditor(groupEditorState);

                if (validationResult.isValid) {
                    return Observable.concat(
                        Observable.of(updateRateCorrectionsEditorValidation(action.id, validationResult.errors)),
                        Observable.of(
                            updateGroupRateCorrections(
                                action.id,
                                mapEditorRateCorrectionsToRateCorrections(groupEditorState)
                            )
                        )
                    );
                } else {
                    return Observable.of(updateRateCorrectionsEditorValidation(action.id, validationResult.errors));
                }
            }
        )
    );
};

const saveOutdoorMapActionEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_OUTDOOR_MAP_ACTION),
        mergeMap((action: SaveOutdoorMapAction) => {
            const state = store$.value;
            const { checkedByRegion = {}, groupId } = state.outdoorMapEditor;

            return [
                updateGroupOutdoorMap(groupId, checkedByRegion),
                closeOutdoorMapEditor(),
                clearOutdoorErrors(groupId)
            ];
        })
    );
};

const saveIndoorMapActionEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_INDOOR_MAP_ACTION),
        mergeMap((action: SaveIndoorMapAction) => {
            const state = store$.value;
            const { checkedByRegion = {}, groupId } = state.indoorMapEditor;
            const screenGroupsByRegion = filterGroupIndoorMapData(state, groupId).screenGroupsByRegion;

            return [
                updateGroupIndoorMap(groupId, checkedByRegion, screenGroupsByRegion),
                clearIndoorErrors(groupId),
                closeIndoorMapEditor()
            ];
        })
    );
};

const saveAudienceTargetingsEpic: DnaEpic = (action$, store$, deps): Observable<Action> => {
    return action$.pipe(
        ofType(SAVE_AUDIENCE_TARGETINGS),
        mergeMap((action: SaveAudienceTargetings) => {
            const state = store$.value;
            const audience = {};
            const templateName = state.audienceTargetingsEditor[action.id].templateName;

            if (state.cryptaSocialDemoEditor[action.id]) {
                audience[AudienceTargetingsType.socialDemo] = state.cryptaSocialDemoEditor[action.id].checkedItems;
            }

            if (state.cryptaGroupFamilyEditor[action.id]) {
                audience[AudienceTargetingsType.family] = state.cryptaGroupFamilyEditor[action.id].checkedItems;
            }

            if (state.cryptaBehavioralEditor[action.id]) {
                audience[AudienceTargetingsType.behaviors] = state.cryptaBehavioralEditor[action.id].checkedItems;
            }

            if (state.interestsTargetingEditor[action.id]) {
                audience[AudienceTargetingsType.interests] = state.interestsTargetingEditor[action.id].value;
            }

            if (state.retargetingAudienceEditor[action.id]) {
                if (state.retargetingAudienceEditor[action.id].isDeleting) {
                    audience[AudienceTargetingsType.audienceMetrics] = undefined;
                } else {
                    audience[AudienceTargetingsType.audienceMetrics] = state.retargetingAudienceEditor[action.id];
                }
            }

            return [
                updateAudienceTargetings(action.id, audience, templateName),
                closeAudienceTargetings(action.id, Object.keys(audience))
            ];
        })
    );
};

const savePITagsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_PI_TAGS_EDITOR),
        mergeMap((action: SavePITagsEditor) => {
            const state = store$.value;
            const piTags = state.piTagsEditor[action.id];

            return [
                saveGroupPITagsEditor(action.id, piTags.pageGroupTags, piTags.targetTags),
                closePITagsEditor(action.id)
            ];
        })
    );
};

const openGroupAudienceTargetingsEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_GROUP_AUDIENCE_TARGETINGS_EDITOR),
        mergeMap((action: OpenGroupAudienceTargetingsEditorAction) => {
            const { editGroups } = store$.value;
            const groupValue = editGroups.groups[action.groupId].value;

            if (
                groupValue.campaignType !== CampaignType.CPM_BANNER &&
                groupValue.campaignType !== CampaignType.TEXT &&
                groupValue.campaignType !== CampaignType.CPM_PRICE
            ) {
                throw Error(`В кампании с типом ${groupValue.campaignType} нет профиля пользователей`);
            }

            if (groupValue.audienceTargetings === undefined) {
                return EMPTY;
            }

            const resultActions: Action[] = [...calculateOpenedCategory(action.groupId, groupValue)];
            const audienceMetrics = groupValue.audienceTargetings[AudienceTargetingsType.audienceMetrics];

            if (audienceMetrics !== undefined) {
                resultActions.push(openAudienceRetargetingEditor(action.groupId, audienceMetrics));
            }

            resultActions.push(openAudienceTargetingsEditor(action.groupId, groupValue.audienceTargetingsTemplateName));

            return resultActions;
        })
    );
};

const openGroupPITagsEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(OPEN_GROUP_PI_TAGS_EDITOR),
        mergeMap((action: OpenGroupPITagsEditorAction) => {
            const { editGroups } = store$.value;
            const groupValue = editGroups.groups[action.groupId].value;

            if (groupValue.piTagsEditor === undefined) {
                return EMPTY;
            }

            return of(
                openPITagsEditor(
                    action.groupId,
                    groupValue.piTagsEditor.pageGroupTags,
                    groupValue.piTagsEditor.targetTags
                )
            );
        })
    );
};

const saveCryptaAudioGenresEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_CRYPTA_AUDIO_GENRES),
        mergeMap((action: SaveCryptaAudioGenres) => {
            const state = store$.value;
            const audioGenresItems = state.cryptaAudioGenresEditor[action.id].checkedItems;

            return of(updateGroupAudienceCryptaAudioGenres(action.id, audioGenresItems));
        })
    );
};

const updateCryptaAudioGenresEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES),
        mergeMap((action: UpdateGroupAudienceCryptaAudioGenres) => {
            return of(closeCryptaAudioGenresEditor(action.id));
        })
    );
};

const resetAudienceTargetingsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(RESET_AUDIENCE_TARGETINGS),
        mergeMap((action: ResetAudienceTargetings) => {
            const state = store$.value;
            const actions = compact([
                state.interestsTargetingEditor[action.id] && resetInterestsTargetingEditor(action.id),
                state.cryptaBehavioralEditor[action.id] && resetCryptaBehavioralEditor(action.id),
                state.cryptaGroupFamilyEditor[action.id] && resetCryptaGroupFamilyEditor(action.id),
                state.cryptaSocialDemoEditor[action.id] && resetCryptaSocialDemoEditor(action.id),
                state.retargetingAudienceEditor[action.id] && resetAudienceRetargetingEditor(action.id)
            ]);

            return actions;
        })
    );
};

const closeAudienceTargetingsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CLOSE_AUDIENCE_TARGETINGS),
        mergeMap((action: CloseAudienceTargetingsAction) => {
            const value = action.value;

            const actions = compact([
                value.includes(AudienceTargetingsType.socialDemo) && closeCryptaSocialDemoEditor(action.id),
                value.includes(AudienceTargetingsType.interests) && closeInterestsTargetingEditor(action.id),
                value.includes(AudienceTargetingsType.behaviors) && closeCryptaBehavioralEditor(action.id),
                value.includes(AudienceTargetingsType.family) && closeCryptaGroupFamilyEditor(action.id)
            ]);

            return actions;
        })
    );
};

const leaveGroupsEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.ofType(LEAVE_GROUPS_EDIT).mergeMap((action: LeaveGroupsEdit) => {
        const state = store$.value;
        const {
            commonData: {
                regions,
                client: { translocalGeotreeType },
                login,
                fromOldInterface,
                features
            },
            editGroups
        } = state;
        const accessRights = selectAccessRights(state);
        const operatorFeatures = selectOperatorFeatures(state);
        const editableTypes = selectEditableGroupTypes({ operatorFeatures, accessRights });
        const regionTree: Region[] = regions
            ? RegionsHelpers.getRegionsTree(regions, LangUtils.getCurrentLang(), translocalGeotreeType)
            : [];

        const groupsData = getGroupsValueFromOpenedEditors(
            state.editGroups.groups,
            state.rateCorrectionsEditor,
            state.regionsEditor,
            state.retargetingEditor,
            state.keywordsEditor.editors,
            state.minusPhraseLibraryEditor,
            state.editPageItemNameEditor,
            state.audienceTargetingsEditor,
            state.interestsTargetingEditor,
            state.cryptaSocialDemoEditor,
            state.cryptaGroupFamilyEditor,
            state.cryptaAudioGenresEditor,
            state.cryptaBehavioralEditor,
            state.retargetingAudienceEditor,
            state.piTagsEditor,
            state.targetTagsEditor,
            regionTree,
            editableTypes,
            features
        );

        const wasDataChanged = Object.keys(groupsData).some(editGroupKey => {
            return !isEqual(groupsData[editGroupKey].value, groupsData[editGroupKey].serverValue);
        });
        const { location } = action;

        let leavePage$: Array<Action>;

        if (fromOldInterface) {
            const cid = state.editGroups.campaignsIds[0];

            if (cid === undefined) {
                throw new Error('не задан номер кампании');
            }

            leavePage$ = [
                changeLocationHard(UrlHelpers.getShowCampaignUrl(cid, login)),
                resetGroupsEdit(),
                resetRetargetingEditors(),
                resetRateCorrectionsEditors()
            ];
        } else {
            const isCampaignCpmBanner = some(
                editGroups.groups,
                group =>
                    group.value.campaignType === CampaignType.CPM_BANNER ||
                    group.value.campaignType === CampaignType.CPM_PRICE
            );
            const typeLocation = typeof location !== 'string' ? location.query : { ulogin: login };
            const calcType = isCampaignCpmBanner ? CampaignsCalcTypeFilter.CPM : CampaignsCalcTypeFilter.CPC;

            leavePage$ = [
                changeLocation(typeof location !== 'string' ? location.pathname : location, {
                    ...typeLocation,
                    [GridScreenUrlSearchQueryParams.calcType]: calcType
                }),
                resetGroupsEdit(),
                resetRetargetingEditors(),
                resetRateCorrectionsEditors(),
                resetTargetTagsEditors()
            ];
        }
        const isInternal = Object.keys(groupsData).some(key => {
            if (groupsData[key].value.campaignType) {
                return [
                    CampaignType.INTERNAL_DISTRIB,
                    CampaignType.INTERNAL_FREE,
                    CampaignType.INTERNAL_AUTOBUDGET
                ].includes(groupsData[key].value.campaignType as CampaignType);
            }

            return false;
        });

        if (wasDataChanged && action.showConfirm) {
            return Observable.fromPromise(
                confirm({
                    message: isInternal ? i18n('warning-about-changes-internal') : i18n('warning-about-changes')
                })
            )
                .mergeMap(() => {
                    return leavePage$;
                })
                .catch(() => EMPTY);
        }

        return leavePage$;
    });
};

const resetGroupsEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(RESET_GROUPS_EDIT),
        map(
            (action: ResetGroupsEdit): ResetRegionsEditor => {
                return resetRegionsEditor();
            }
        )
    );
};

const cancelGroupEditorRegionsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(CANCEL_GROUP_EDITOR_REGIONS).mergeMap((action: CancelGroupEditorRegions): Action[] => {
        return [
            cancelChangesRegionsEditor(action.groupId, action.selectedMap),
            clearErrorsGroupEditorRegions(action.groupId)
        ];
    });
};

const saveGroupEditorRegionEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(SAVE_GROUP_EDITOR_REGIONS).map(
        (action: SaveGroupEditorRegions): Action => {
            const hasSelectedRegions = some(action.selectedMap, item => item === true);

            if (!hasSelectedRegions) {
                return showGroupEditorRegionsErrors(action.groupId, [
                    {
                        text: i18n('geo-empty-regions')
                    }
                ]);
            }

            return updateGroupEditorRegions(action.groupId, action.selectedMap, action.tree);
        }
    );
};

const updateGroupEditorRegionEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(UPDATE_GROUP_EDITOR_REGIONS).map(
        (action: SaveGroupEditorRegions): Action => {
            return closeRegionsEditor(action.groupId);
        }
    );
};

const deleteRetargetingListItemFromGroupEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(DELETE_RETARGETING_LIST_ITEM).map(
        (action: DeleteRetargetingListItem): DeleteRetargetingConditionFromGroup => {
            return deleteRetargetingConditionFromGroup(action.id, action.index);
        }
    );
};

const updateRetargetingListItemEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(UPDATE_RETARGETING_LIST_ITEM, ADD_RETARGETING_LIST_ITEM)
        .map((action: UpdateRetargetingListItem) => {
            return updateRetargetingConditions(action.conditionData);
        });
};

const openGroupFilterInspectorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_GROUP_FILTER_INSPECTOR),
        mergeMap((action: OpenGroupFilterInspectorAction) => {
            const {
                editGroups: {
                    groupFilterInspector: { feedId }
                },
                commonData: { login }
            } = store$.value;

            if (!feedId) {
                throw new Error('the group has an invalid feedId');
            }

            return from(deps.groupsDataProvider.getFeeds(login)).pipe(
                map(result => result.data.client.feeds.rowset),
                mergeMap((originFeeds: GroupsQueryForEdit_client_feeds_rowset[]) => {
                    const { feeds } = mapFeeds(originFeeds);

                    const { categories, feedType, businessType } = feeds[feedId];

                    return from(deps.groupsDataProvider.getSmartFeedSchema(businessType, feedType)).pipe(
                        mergeMap(({ data }) => {
                            return from([
                                setFilterFeedCategories(categories),
                                updateFeedFilterSchema(mapFeedFilterSchemaFromApiResponse(data))
                            ]);
                        }),
                        catchError(() => EMPTY)
                    );
                })
            );
        })
    );
};

const DELAY_TIME_TO_START = 0;
const TIME_TO_REPEAT_REQUEST = 3000;

const pollingFeedsData: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PERFORMANCE_SET_VISIBLE_GROUP_BANNER_POPUP_FEED),
        filter((action: SetVisibleGroupBannerPopupFeedAction) => action.isVisible),
        switchMap(() => {
            const login = store$.value.commonData.login;

            return timer(DELAY_TIME_TO_START, TIME_TO_REPEAT_REQUEST).pipe(
                takeUntil(
                    action$.pipe(
                        ofType(PERFORMANCE_SET_VISIBLE_GROUP_BANNER_POPUP_FEED),
                        filter((action: SetVisibleGroupBannerPopupFeedAction) => {
                            const { groupsIds, groups } = store$.value.editGroups;
                            let noGroupHasOpenPopup = true;

                            for (let i = 0; i < groupsIds.length; i++) {
                                const groupId = groupsIds[i];
                                const group = groups[groupId];

                                // если у группы есть открытый попап с выбором фида, то нужно отфильтровать данный экшен
                                if (
                                    group.value.campaignType === CampaignType.PERFORMANCE &&
                                    group.value.feedsPopupIsOpen
                                ) {
                                    noGroupHasOpenPopup = false;
                                    break;
                                }
                            }

                            // эпик будет слушать "takeUntil" до тех пор, пока не придет экшен на закрытие попапа и не будет других открытых попапов
                            return !action.isVisible && noGroupHasOpenPopup;
                        })
                    )
                ),
                exhaustMap(() =>
                    from(deps.groupsDataProvider.getFeeds(login)).pipe(
                        map(result => result.data.client.feeds.rowset),
                        map((originFeeds: GroupsQueryForEdit_client_feeds_rowset[]) => {
                            const { feedsIds, feeds } = mapFeeds(originFeeds);
                            return updateFeeds(feedsIds, feeds);
                        })
                    )
                ),
                catchError(() => EMPTY)
            );
        })
    );
};

// todo удалить после открытия фичи на 100% DIRECT-109443
const createTextGroupEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CREATE_TEXT_AD_GROUP),
        mergeMap(({ campaignId, name, replaceLocation }: CreateTextAdGroupAction) => {
            const {
                commonData: { login }
            } = store$.value;
            return from(
                createTextAdGroup(deps.groupsMutationExecutor, {
                    login,
                    campaignId,
                    name
                })
            ).pipe(
                map(({ adGroupId }) => goToGroupsEdit(login, [campaignId], [adGroupId], true, replaceLocation)),
                catchError((err: Error | ApiMethodExecutingError) => {
                    const message =
                        err instanceof ApiMethodExecutingError
                            ? err.getMessage()
                            : err.message || i18n('create-group-error');

                    showNoticeMessage(NoticeType.error, message);
                    return EMPTY;
                })
            );
        })
    );
};

const createMcBannerGroupEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CREATE_MC_BANNER_AD_GROUP),
        mergeMap(({ campaignId, name, replaceLocation }: CreateMcBannerAdGroupAction) => {
            const {
                commonData: { login }
            } = store$.value;
            return from(
                createMcBannerAdGroup(deps.groupsMutationExecutor, {
                    login,
                    campaignId,
                    name
                })
            ).pipe(
                map(({ adGroupId }) => goToGroupsEdit(login, [campaignId], [adGroupId], true, replaceLocation)),
                catchError((err: Error | ApiMethodExecutingError) => {
                    const message =
                        err instanceof ApiMethodExecutingError
                            ? err.getMessage()
                            : err.message || i18n('create-group-error');

                    showNoticeMessage(NoticeType.error, message);
                    return EMPTY;
                })
            );
        })
    );
};

const createSmartGroupEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CREATE_SMART_AD_GROUP),
        mergeMap(({ campaignId, feedId, name, mockGroupId }: CreateSmartAdGroupAction) => {
            const {
                commonData: {
                    login,
                    regions,
                    client: { translocalGeotreeType }
                }
            } = store$.value;
            const smartInput = {
                campaignId,
                feedId,
                name
            };

            return from(deps.groupsMutationExecutor.createSmartGroup(smartInput, login)).pipe(
                mergeMap(
                    (result): Observable<Action> => {
                        const { data } = result;
                        if (!data) {
                            throw new Error();
                        }

                        const { validationResult } = data.addSmartAdGroups;
                        if (validationResult && validationResult.errors.length) {
                            const errorMapper = createSmartGroupAddHandleErrorMapper(mockGroupId);
                            const validationErrors = errorMapper.mapServerErrorsToClientError(validationResult.errors);

                            return of(
                                showGroupsPartialSaveResult({
                                    errors: validationErrors,
                                    updatedGroupIds: []
                                })
                            );
                        }

                        const [createdGroupData] = data.addSmartAdGroups.addedAdGroupItems;

                        if (!createdGroupData || !createdGroupData.adGroupId) {
                            throw new Error();
                        }

                        const { adGroupId } = createdGroupData;

                        const regionsTree =
                            RegionsHelpers.getRegionsTree(
                                regions || {},
                                LangUtils.getCurrentLang(),
                                translocalGeotreeType
                            ) || [];

                        GroupIdsForGroupsEditHelpers.setCampAndGroupIds(login, [adGroupId], [campaignId]);

                        const group = store$.value.editGroups.groups[mockGroupId].value;
                        let regionIds = [0];
                        switch (group.campaignType) {
                            case CampaignType.PERFORMANCE:
                                regionIds = group.regions.regionIds;
                                break;
                            default:
                                break;
                        }

                        return merge(
                            of(
                                changeLocation(
                                    UrlHelpers.getGroupsEditUrl({
                                        [GroupsScreenUrlSearchQueryParams.campaignsIds]: [campaignId],
                                        [GroupsScreenUrlSearchQueryParams.groupsIds]: [adGroupId],
                                        [GroupsScreenUrlSearchQueryParams.isNew]: undefined,
                                        [GroupsScreenUrlSearchQueryParams.isNewEmptyGroup]: store$.value.editGroups
                                            .isNewEmptyGroup
                                            ? '1'
                                            : undefined,
                                        [GroupsScreenUrlSearchQueryParams.ulogin]: login
                                    }),
                                    undefined,
                                    false
                                )
                            ),
                            of(moveNewGroupToCreated(mockGroupId, adGroupId, feedId)),
                            !regionIds.length
                                ? from(deps.groupsDataProvider.getGroups(login, [adGroupId], [campaignId])).pipe(
                                      mergeMap(({ data: queryData }) => {
                                          const rawGroup = queryData.client.adGroups.rowset.find(
                                              ({ id }) => id === adGroupId
                                          );
                                          if (!rawGroup) {
                                              throw new Error();
                                          }
                                          return from([
                                              saveGroupEditorRegions(
                                                  adGroupId,
                                                  RegionsHelpers.getSelectedMapFromRawRegionData(
                                                      regionsTree,
                                                      rawGroup.regionsInfo.regionIds
                                                  ),
                                                  regionsTree
                                              ),
                                              saveGroups(false)
                                          ]);
                                      })
                                  )
                                : of(saveGroups(false))
                        );
                    }
                ),
                catchError(
                    (error: Error | ApiMethodExecutingError): Observable<ShowGroupEditErrorAction> => {
                        const message =
                            error instanceof ApiMethodExecutingError
                                ? error.getMessage()
                                : error.message || i18n('create-group-error');

                        return Observable.of(showGroupEditError(message));
                    }
                )
            );
        })
    );
};

const saveGroupMinusPhrasesEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_GROUP_MINUS_PHRASES_EDITOR),
        mergeMap(
            (action: SaveGroupMinusPhrasesEditorAction): Observable<Action> => {
                const state = store$.value;

                const {
                    data: {
                        [action.groupId]: { minusKeywordsEditor }
                    }
                } = state.minusPhraseLibraryEditor;

                const offset = 0;
                const constants = state.commonData.constants;

                if (!constants) {
                    return EMPTY;
                }

                const limit = constants.minusPhraseLibraryConstants.maxLibraryPacksCount;

                // Заново запросить минус-фразы на случай, если были выбраны удаленные элементы
                return from(deps.minusPhraseLibraryDataProvider.fetchMinusPhraseLibraryData(offset, limit)).pipe(
                    mergeMap((response: FetchMinusPhraseLibraryDataResult): Action[] => {
                        const updatedLibraryItems = mapFetchResultToLibraryItem(response.data);

                        return [
                            groupApplyChangedMinusPhraseEditor(
                                action.groupId,
                                minusKeywordsEditor,
                                updatedLibraryItems
                            ),
                            closeMinusPhrasesEditor(action.groupId)
                        ];
                    })
                );
            }
        )
    );
};

const handleGroupsEditMassActionsEpic: DnaEpic = action$ => {
    return action$.pipe(
        ofType(HANDLE_GROUPS_EDIT_MASS_ACTIONS),
        mergeMap(
            ({ action }: HandleGroupsEditMassActions): Observable<Action> => {
                switch (action) {
                    case GroupsEditMassActionItemType.minusPhrase:
                        return of(openMinusPhraseGroupsMassActions(MassActionId.groupsEdit, []));
                    default:
                        return EMPTY;
                }
            }
        )
    );
};

const groupDataUpdateKeywordsFromEditorEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(GROUP_DATA_UPDATE_KEYWORDS_FROM_EDITOR),
        map(
            (action: GroupDataUpdateKeywordsFromEditorAction): Action => {
                const keywordsEditorData = selectKeywordsEditorData(store$.value, action.editorId);

                return groupDataUpdateKeywords(action.groupId, keywordsEditorData.value);
            }
        )
    );
};

const changePageGroupTagsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PAGE_GROUP_TAGS_CHANGE),
        mergeMap((action: PageGroupTagsChange) => {
            const {
                editGroups: { groups }
            } = store$.value;

            const { errors } = groups[action.id];

            if (!errors.pageGroupTags) {
                return EMPTY;
            }

            return of(clearErrorPageGroupTags(action.id));
        })
    );
};

const changeTargetTagsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(TARGET_TAGS_CHANGE),
        mergeMap((action: TargetTagsChange) => {
            const {
                editGroups: { groups }
            } = store$.value;

            const { errors } = groups[action.id];

            if (!errors.targetTags) {
                return EMPTY;
            }

            return of(clearErrorTargetTags(action.id));
        })
    );
};

export const groupsEditEpic = combineEpics(
    applySelectorWizardForGroupsEditEpic,
    cancelGroupEditorRegionsEpic,
    changePageGroupTagsEpic,
    changeTargetTagsEpic,
    closeAudienceTargetingsEpic,
    createMcBannerGroupEpic,
    createSmartGroupEpic,
    createTextGroupEpic,
    deleteRetargetingListItemFromGroupEpic,
    getCampaignBasicDataEpic,
    getCpcGroupsForEditEpic,
    getCpmGroupsForEditEpic,
    getGroupsForEditEpic,
    getInternalGroupsForEditEpic,
    getRecommendationsForGroupsEditEpic,
    getRetargetingConditionsForGroupsEditEpic,
    groupDataUpdateKeywordsFromEditorEpic,
    handleGroupsEditMassActionsEpic,
    leaveGroupsEditEpic,
    openEditGroupPhrasesSelectorEpic,
    openGroupAudienceTargetingsEditorEpic,
    openGroupPITagsEditorEpic,
    openGroupFilterInspectorEpic,
    openGroupRetargetingEditorEpic,
    openPhrasesSelectorWizardForGroupsEditEpic,
    openRecommendationsModalForGroupsEditEpic,
    pollingFeedsData,
    resetAudienceTargetingsEpic,
    resetGroupsEditEpic,
    saveAudienceTargetingsEpic,
    saveCpmGroupsEpic,
    saveCryptaAudioGenresEpic,
    saveGroupEditorRegionEpic,
    saveGroupMinusPhrasesEditorEpic,
    saveGroupRateCorrectionEditorEpic,
    saveGroupRetargetingEditorEpic,
    saveGroupsEpic,
    saveIndoorMapActionEpic,
    saveOutdoorMapActionEpic,
    savePhrasesSelectorPhrasesEpic,
    savePITagsEpic,
    updateCryptaAudioGenresEpic,
    updateGroupEditorRegionEpic,
    updateGroupNameEpic,
    updateGroupRetargetingEditorEpic,
    updateGroupTypeEpic,
    updateRetargetingListItemEpic,
    updateShowConditionTypeEpic
);
